import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { getCurrentYearMonthKst, toList } from './utils';

// 분류가 비어 있는 상품의 열 이름. 전에는 내부/외부로 '제주'/'영화' 를 갈랐는데,
//   그 이름은 운영사 사업라인이라 손님 회사 화면에서도 그대로 보였다. 한 가지로 통일한다(2026-09-20).
const FALLBACK_CATEGORY = '미분류';

function productKey(id, name) {
  return `product:${id || name || 'unknown'}`;
}

function categoryLabel(category) {
  const label = String(category || '').trim();
  return label || FALLBACK_CATEGORY;
}

function categoryKey(category) {
  return `category:${categoryLabel(category)}`;
}

function sortColumns(a, b) {
  return String(a.label || '').localeCompare(String(b.label || ''), 'ko-KR');
}

function normalizeProductColumns(columns) {
  const seen = new Set();
  return (columns || [])
    .map((item) => ({
      key: productKey(item.id, item.label || item.name),
      id: item.id,
      label: String(item.label || item.name || '상품').trim(),
      category: String(item.category || '').trim(),
    }))
    .filter((item) => {
      if (!item.label || seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    })
    .sort(sortColumns);
}

function normalizeCategoryColumns(columns) {
  const seen = new Set();
  return (columns || [])
    .map((item) => {
      const label = categoryLabel(item.label || item.name);
      return { key: categoryKey(label), label };
    })
    .filter((item) => {
      if (!item.label || seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    })
    .sort(sortColumns);
}

function collectColumnsFromContracts(contracts) {
  const productMap = new Map();
  const categoryMap = new Map();

  (contracts || []).forEach((contract) => {
    // 서비스 상품도 컬럼·집계에 포함 (2026-09-01 사용자 확정: 계약+서비스 합산 장수)
    const items = Array.isArray(contract.contract_items) ? contract.contract_items : [];
    items.forEach((item) => {
      const pKey = productKey(item.ticketTypeId ?? item.ticket_type_id, item.ticketTypeName ?? item.ticket_type_name);
      if (!productMap.has(pKey)) {
        productMap.set(pKey, {
          key: pKey,
          id: item.ticketTypeId ?? item.ticket_type_id,
          label: String(item.ticketTypeName ?? item.ticket_type_name ?? '상품').trim(),
          category: categoryLabel(item.category),
        });
      }

      const cLabel = categoryLabel(item.category);
      const cKey = categoryKey(cLabel);
      if (!categoryMap.has(cKey)) {
        categoryMap.set(cKey, { key: cKey, label: cLabel });
      }
    });
  });

  return {
    productColumns: Array.from(productMap.values()).sort(sortColumns),
    categoryColumns: Array.from(categoryMap.values()).sort(sortColumns),
  };
}

// includeService (2026-09-01 사용자 확정): 마케터별=서비스 수량 포함(계약 200+서비스 20=220장),
//   상담자별(TM)=서비스 제외 유지.
function computeRows(contracts, groupKey, { includeService = false } = {}) {
  const map = new Map();

  (contracts || []).forEach((contract) => {
    const name = String(contract[groupKey] || '미지정').trim() || '미지정';
    if (!map.has(name)) {
      map.set(name, {
        name,
        products: {},
        categories: {},
        total: 0,
      });
    }

    const row = map.get(name);
    const all = Array.isArray(contract.contract_items) ? contract.contract_items : [];
    const items = includeService ? all : all.filter((item) => !item.isService);
    items.forEach((item) => {
      const qty = Number(item.quantity) || 0;
      const weight = Number(item.weight ?? 1) || 1;
      const weightedQty = Number(item.weightedQuantity ?? item.weighted_quantity ?? (qty * weight)) || 0;
      const pKey = productKey(item.ticketTypeId ?? item.ticket_type_id, item.ticketTypeName ?? item.ticket_type_name);
      const cKey = categoryKey(item.category);

      row.products[pKey] = (row.products[pKey] || 0) + qty;
      row.categories[cKey] = (row.categories[cKey] || 0) + weightedQty;
      row.total += weightedQty;
    });
  });

  return Array.from(map.values())
    .map((row) => ({ ...row, total: Math.round(row.total * 100) / 100 }))
    .sort((a, b) => b.total - a.total);
}

// byMonthUrl — 월별 계약 주소(2026-10-19). 기본 = 본사 위젯 주소, 회사별·통합 계약 위젯은 그 화면의 '…/contracts/by-month'.
const HQ_BY_MONTH_URL = '/api/admin/situation/contracts-by-month';

// summaryCode(2026-10-24): 위젯 메뉴가 없는 손님 테넌트 직원 — 계약 원본 대신 카드 요약 길(/api/my/widget-summaries)에서
//   서버가 같은 식으로 묶은 그달 표(rows·열)를 받는다. 없으면 종전처럼 byMonthUrl 의 계약을 받아 여기서 묶는다.
export function useContractMonth(byMonthUrl = HQ_BY_MONTH_URL, { summaryCode = null } = {}) {
  const { year: initY, month: initM } = getCurrentYearMonthKst();
  const [year, setYear] = useState(initY);
  const [month, setMonth] = useState(initM);
  const [contracts, setContracts] = useState([]);
  const [productColumns, setProductColumns] = useState([]);
  const [categoryColumns, setCategoryColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const ym = `${year}-${String(month).padStart(2, '0')}`;
    const url = summaryCode
      ? `/api/my/widget-summaries?codes=${summaryCode}&month_${summaryCode}=${ym}`
      : `${byMonthUrl}?year=${year}&month=${month}`;
    apiClient.get(url)
      .then((res) => {
        if (cancelled) return;
        if (summaryCode) {
          const p = res?.data?.data?.[summaryCode] || {};
          setContracts([]);
          setSummaryRows(Array.isArray(p.rows) ? p.rows : []);
          setProductColumns(Array.isArray(p.productColumns) ? p.productColumns : []);
          setCategoryColumns(Array.isArray(p.categoryColumns) ? p.categoryColumns : []);
          return;
        }
        const rows = toList(res);
        const payload = res?.data || {};
        const fallbackColumns = collectColumnsFromContracts(rows);
        const apiProductColumns = normalizeProductColumns(payload.product_columns);
        const apiCategoryColumns = normalizeCategoryColumns(payload.category_columns);

        setContracts(rows);
        setProductColumns(apiProductColumns.length ? apiProductColumns : fallbackColumns.productColumns);
        setCategoryColumns(apiCategoryColumns.length ? apiCategoryColumns : fallbackColumns.categoryColumns);
      })
      .catch(() => {
        if (!cancelled) {
          setContracts([]);
          setProductColumns([]);
          setCategoryColumns([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [year, month, byMonthUrl, summaryCode]);

  // 마케터별=서비스 포함 / 상담자별(TM)=서비스 제외 (2026-09-01 사용자 확정)
  const marketerRows = useMemo(() => (summaryCode ? summaryRows : computeRows(contracts, 'sales_person_name', { includeService: true })), [contracts, summaryCode, summaryRows]);
  const tmRows = useMemo(() => (summaryCode ? summaryRows : computeRows(contracts, 'user_name', { includeService: false })), [contracts, summaryCode, summaryRows]);

  function goPrev() {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  }

  function goNext() {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  }

  // year/month 동시 변경 — UnifiedCard monthNavigate('YYYY-MM') 와 호환.
  function setYearMonth(y, m) {
    if (!Number.isFinite(y) || !Number.isFinite(m)) return;
    setYear(y);
    setMonth(m);
  }

  return {
    year,
    month,
    goPrev,
    goNext,
    setYearMonth,
    loading,
    marketerRows,
    tmRows,
    productColumns,
    categoryColumns,
  };
}
